import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import './Login.css';

type AuthMode = 'login' | 'register' | 'verify' | 'forgot';

const Login: React.FC = () => {
  const [mode, setMode] = useState<AuthMode>('login');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: '',
    referral_code: '',
    first_name: '',
    last_name: ''
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [remainingResends, setRemainingResends] = useState(3);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState(false);

  const { login, register, verifyEmail, resendCode, forgotPassword } = useAuth();
  const navigate = useNavigate();

  // Cooldown timer effect
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits and limit to 6 characters
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (mode === 'login') {
        const success = await login(formData.email, formData.password);
        if (success) {
          navigate('/');
        } else {
          setError('Invalid credentials');
        }
      } else if (mode === 'register') {
        if (formData.password.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
        const result = await register(formData.email, formData.password, formData.username, formData.referral_code, formData.first_name, formData.last_name);
        if (result.success) {
          if (result.emailVerificationSkipped) {
            // Email verification was bypassed - user is now logged in
            navigate('/');
          } else if (result.email) {
            // Move to verification step
            setPendingEmail(result.email);
            setMode('verify');
            setResendCooldown(60); // Start with 60 second cooldown
          }
        } else {
          setError(result.error || 'Registration failed. Email might already be in use.');
        }
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (verificationCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await verifyEmail(pendingEmail, verificationCode);
      if (result.success) {
        navigate('/');
      } else if (result.expired || result.locked) {
        // Need to start over
        setError(result.error || 'Verification failed. Please register again.');
        resetToRegister();
      } else {
        setError(result.error || 'Invalid code. Please try again.');
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0 || remainingResends <= 0) return;

    setLoading(true);
    setError('');

    try {
      const result = await resendCode(pendingEmail);
      if (result.success) {
        setResendCooldown(60);
        if (result.remainingResends !== undefined) {
          setRemainingResends(result.remainingResends);
        }
        setError(''); // Clear any previous error
      } else if (result.expired || result.maxResends) {
        setError(result.error || 'Please register again.');
        resetToRegister();
      } else if (result.waitTime) {
        setResendCooldown(result.waitTime);
      } else {
        setError(result.error || 'Failed to resend code.');
      }
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetToRegister = () => {
    setMode('register');
    setVerificationCode('');
    setPendingEmail('');
    setResendCooldown(0);
    setRemainingResends(3);
  };

  const goBackToRegister = () => {
    resetToRegister();
    setFormData({ ...formData, password: '' }); // Clear password for security
    setError('');
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await forgotPassword(forgotEmail);
      if (result.success) {
        setForgotSuccess(true);
      } else {
        setError(result.error || 'Request failed');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const goBackToLogin = () => {
    setMode('login');
    setForgotEmail('');
    setForgotSuccess(false);
    setError('');
  };

  // Verification Screen
  if (mode === 'verify') {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <img src="/hugemajicrealmlogo.png" alt="The Majic Realm" className="login-logo" />
            <p>Verify your email</p>
          </div>

          <div className="verification-info">
            <p>We sent a 6-digit code to:</p>
            <p className="verification-email">{pendingEmail}</p>
          </div>

          <form onSubmit={handleVerifySubmit} className="login-form">
            {error && <div className="error-message">{error}</div>}

            <div className="form-group">
              <label htmlFor="code">Verification Code</label>
              <input
                type="text"
                id="code"
                name="code"
                value={verificationCode}
                onChange={handleCodeChange}
                placeholder="Enter 6-digit code"
                className="verification-code-input"
                autoComplete="one-time-code"
                inputMode="numeric"
                maxLength={6}
              />
            </div>

            <button
              type="submit"
              className="submit-button"
              disabled={loading || verificationCode.length !== 6}
            >
              {loading ? 'Verifying...' : 'Verify Email'}
            </button>
          </form>

          <div className="verification-actions">
            <button
              type="button"
              className="resend-button"
              onClick={handleResendCode}
              disabled={resendCooldown > 0 || remainingResends <= 0 || loading}
            >
              {resendCooldown > 0
                ? `Resend code (${resendCooldown}s)`
                : remainingResends <= 0
                ? 'No resends left'
                : 'Resend code'}
            </button>
            {remainingResends > 0 && remainingResends < 3 && (
              <p className="resend-count">{remainingResends} resend{remainingResends !== 1 ? 's' : ''} remaining</p>
            )}
            <button
              type="button"
              className="back-button"
              onClick={goBackToRegister}
            >
              Wrong email? Go back
            </button>
          </div>

          <div className="login-footer">
            <Link to="/" className="back-link">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Forgot Password Screen
  if (mode === 'forgot') {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <img src="/hugemajicrealmlogo.png" alt="The Majic Realm" className="login-logo" />
            <p>Reset your password</p>
          </div>

          {forgotSuccess ? (
            <>
              <div className="success-message">
                <p>If an account exists with that email, a reset link has been sent.</p>
                <p className="success-note">Please check your email and click the reset link within 20 minutes.</p>
              </div>
              <button
                type="button"
                className="submit-button"
                onClick={goBackToLogin}
              >
                Back to Login
              </button>
            </>
          ) : (
            <>
              <div className="forgot-info">
                <p>Enter your email address and we'll send you a link to reset your password.</p>
              </div>

              <form onSubmit={handleForgotSubmit} className="login-form">
                {error && <div className="error-message">{error}</div>}

                <div className="form-group">
                  <label htmlFor="forgot-email">Email Address</label>
                  <input
                    type="email"
                    id="forgot-email"
                    name="email"
                    value={forgotEmail}
                    onChange={(e) => {
                      setForgotEmail(e.target.value);
                      setError('');
                    }}
                    required
                    placeholder="Enter your email"
                  />
                </div>

                <button
                  type="submit"
                  className="submit-button"
                  disabled={loading || !forgotEmail}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>

              <div className="verification-actions">
                <button
                  type="button"
                  className="back-button"
                  onClick={goBackToLogin}
                >
                  Back to Login
                </button>
              </div>
            </>
          )}

          <div className="login-footer">
            <Link to="/" className="back-link">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Login/Register Screen
  const isLogin = mode === 'login';

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <img src="/hugemajicrealmlogo.png" alt="The Majic Realm" className="login-logo" />
          <p>{isLogin ? 'Welcome back, adventurer!' : 'Join the realm!'}</p>
        </div>

        <div className="login-tabs">
          <button
            className={`tab-button ${isLogin ? 'active' : ''}`}
            onClick={() => {
              setMode('login');
              setError('');
              setFormData({ email: '', password: '', username: '', referral_code: '', first_name: '', last_name: '' });
            }}
          >
            Login
          </button>
          <button
            className={`tab-button ${!isLogin ? 'active' : ''}`}
            onClick={() => {
              setMode('register');
              setError('');
              setFormData({ email: '', password: '', username: '', referral_code: '', first_name: '', last_name: '' });
            }}
          >
            Register
          </button>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="email">{isLogin ? 'Email, Username, or Realm ID' : 'Email'}</label>
            <input
              type={isLogin ? "text" : "email"}
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              placeholder={isLogin ? "Enter email, username, or realm ID" : "Enter your email"}
            />
          </div>

          {!isLogin && (
            <div className="form-group-row">
              <div className="form-group">
                <label htmlFor="first_name">First Name</label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  placeholder="First name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="last_name">Last Name</label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  placeholder="Last name"
                />
              </div>
            </div>
          )}

          {!isLogin && (
            <div className="form-group">
              <label htmlFor="username">Username (optional)</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                placeholder="Choose a username"
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                placeholder="Enter your password"
                minLength={6}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {!isLogin && (
              <small className="form-hint">Password must be at least 6 characters</small>
            )}
          </div>

          {!isLogin && (
            <div className="form-group">
              <label htmlFor="referral_code">Referral Code (optional)</label>
              <input
                type="text"
                id="referral_code"
                name="referral_code"
                value={formData.referral_code}
                onChange={handleInputChange}
                placeholder="Enter referral code if you have one"
              />
            </div>
          )}

          {!isLogin && (
            <p className="terms-notice">
              By creating an account, you agree to our{' '}
              <a href="/realmtos.html" target="_blank" rel="noopener noreferrer">
                Terms of Use and Service
              </a>.
            </p>
          )}

          <button
            type="submit"
            className="submit-button"
            disabled={loading}
          >
            {loading ? 'Please wait...' : (isLogin ? 'Enter the Realm' : 'Create Account')}
          </button>

          {isLogin && (
            <button
              type="button"
              className="forgot-password-link"
              onClick={() => {
                setMode('forgot');
                setError('');
              }}
            >
              Forgot Password?
            </button>
          )}
        </form>

        <div className="login-footer">
          <Link to="/" className="back-link">
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
